import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { Container, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import PopupButton from "../Buttons/PopupButton";
import Scrollbar from "../Other/Scrollbar";
import { formatToCustomLocale } from "../../../../utilities";

/**
 * ViewportMask - Creates a rectangular mask to clip scrollable content
 */
const ViewportMask = React.forwardRef(({ width, height }, ref) => (
  <Graphics
    ref={ref}
    name="viewport_mask"
    draw={(g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(0, 0, width, height);
      g.endFill();
    }}
  />
));

/**
 * BetSelectContent - Scrollable bet selection grid component (backport)
 *
 * 3-column centered grid with scrollbar, using popup_button textures.
 */
const BetSelectContent = ({
  bets,
  selectedBet,
  balance,
  currency,
  onBetSelect,
  viewportConfig,
  gridColumns,
  betButtonConfig,
  buttonSpacing,
  rowSpacing,
}) => {
  const scrollContainerRef = useRef(null);
  const viewportMaskRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // Touch scrolling state
  const touchStartY = useRef(0);
  const lastTouchY = useRef(0);
  const isDragging = useRef(false);

  const verticalPadding = betButtonConfig.height / 2;

  const buttonPositions = useMemo(() => {
    return bets.map((betValue, index) => {
      const row = Math.floor(index / gridColumns);
      const col = index % gridColumns;

      // Centered: calculate total grid width and center it
      const totalWidth =
        (gridColumns - 1) * (betButtonConfig.width + buttonSpacing);
      const startX = -totalWidth / 2;
      const x = startX + col * (betButtonConfig.width + buttonSpacing);
      const y = verticalPadding + row * (betButtonConfig.height + rowSpacing);

      return { x, y };
    });
  }, [
    bets,
    gridColumns,
    buttonSpacing,
    rowSpacing,
    verticalPadding,
    betButtonConfig.width,
    betButtonConfig.height,
  ]);

  // Calculate scroll limits
  const rows = Math.ceil(bets.length / gridColumns);
  const totalContentHeight =
    verticalPadding + rows * (betButtonConfig.height + rowSpacing);
  const maxScrollY = Math.max(
    0,
    totalContentHeight - viewportConfig.viewportHeight,
  );

  // Wheel scroll handler
  const handleScroll = useCallback(
    (event) => {
      const scrollSpeed = 20;
      const newScrollY =
        scrollY + (event.deltaY > 0 ? scrollSpeed : -scrollSpeed);
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));

      if (clampedScrollY !== scrollY) {
        event.preventDefault();
        event.stopPropagation();
        setScrollY(clampedScrollY);
      }
    },
    [scrollY, maxScrollY],
  );

  // Touch scroll handlers
  const handlePointerDown = useCallback((event) => {
    if (event.data.pointerType !== "touch" && event.data.pointerType !== "pen")
      return;
    touchStartY.current = event.data.global.y;
    lastTouchY.current = event.data.global.y;
    isDragging.current = true;
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!isDragging.current) return;
      if (
        event.data.pointerType !== "touch" &&
        event.data.pointerType !== "pen"
      )
        return;

      event.stopPropagation();
      const currentY = event.data.global.y;
      const deltaY = (lastTouchY.current - currentY) * 2.0;
      lastTouchY.current = currentY;

      const newScrollY = scrollY + deltaY;
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));
      setScrollY(clampedScrollY);
    },
    [scrollY, maxScrollY],
  );

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Only apply mask when scrolling is needed — prevents hover clipping when all buttons fit
  useEffect(() => {
    if (scrollContainerRef.current) {
      if (maxScrollY > 0 && viewportMaskRef.current) {
        scrollContainerRef.current.mask = viewportMaskRef.current;
      } else {
        scrollContainerRef.current.mask = null;
      }
    }
  }, [maxScrollY]);

  const scrollbarX = viewportConfig.maxWidth + 8;
  const scrollbarHeight = viewportConfig.viewportHeight;

  return (
    <Container
      name="bet_select_scrollable_content"
      onwheel={handleScroll}
      onpointerdown={handlePointerDown}
      onpointermove={handlePointerMove}
      onpointerup={handlePointerUp}
      onpointercancel={handlePointerUp}
      onpointerleave={handlePointerUp}
      eventMode="static"
      hitArea={
        new PIXI.Rectangle(
          0,
          0,
          viewportConfig.maxWidth,
          viewportConfig.viewportHeight,
        )
      }
    >
      {/* Viewport Mask — only rendered when scrolling is needed */}
      {maxScrollY > 0 && (
        <ViewportMask
          ref={viewportMaskRef}
          width={viewportConfig.maxWidth}
          height={viewportConfig.viewportHeight}
        />
      )}

      {/* Scrollbar */}
      {maxScrollY > 0 && (
        <Scrollbar
          scrollY={scrollY}
          maxScrollY={maxScrollY}
          viewportHeight={scrollbarHeight}
          x={scrollbarX}
          y={0}
          width={4}
        />
      )}

      {/* Bet Buttons Container - centered */}
      <Container
        ref={scrollContainerRef}
        name="bet_buttons_container"
        x={viewportConfig.maxWidth / 2}
        y={-scrollY}
      >
        {bets.map((betValue, index) => {
          const position = buttonPositions[index];
          const isSelected = betValue === selectedBet;
          const isDisabled = betValue > balance;

          // Viewport culling
          const isVisible =
            position.y - scrollY > -betButtonConfig.height &&
            position.y - scrollY <
              viewportConfig.viewportHeight + betButtonConfig.height;

          if (!isVisible) return null;

          return (
            <PopupButton
              key={index}
              x={position.x}
              y={position.y}
              text={formatToCustomLocale(betValue)}
              onClick={() => onBetSelect(betValue)}
              disabled={isDisabled}
              active={isSelected}
              width={betButtonConfig.width}
              height={betButtonConfig.height}
              fontSize={betButtonConfig.fontSize}
              accessibleTitle={`${formatToCustomLocale(betValue)} ${currency}`}
            />
          );
        })}
      </Container>
    </Container>
  );
};

export default BetSelectContent;
